/**
 * The festival in public (docs/plans/FESTIVALS.md Phase 6), as the pages that
 * show it read it: the showliner.io festival page (Tailwind) and the admin
 * app's festival share page (MUI). Two renderers on two design systems, one
 * set of rules for what goes where, so they cannot disagree about which day a
 * stage plays or how a set time reads.
 *
 * The payload is the server's `festivalPublicPolicy.projectPublicFestival`.
 * Pure; imports only the import-free showTimezone.
 */

import { instantToWallClock, normalizeTimeZone } from './showTimezone';

export interface PublicFestivalAct {
  performerId: string;
  name: string;
  profilePicture: string | null;
  setStartTime: string | null;
  setEndTime: string | null;
  /** Share link only. */
  claimable?: boolean;
}

export interface PublicFestivalStage {
  id: string;
  label: string;
  startsAt: string;
  endsAt: string | null;
  live: boolean;
  played: boolean;
  roomCode: string | null;
  venue: { id: string; name: string; profilePicture: string | null } | null;
  acts: PublicFestivalAct[];
}

export interface PublicFestival {
  id: string;
  name: string;
  timezone: string | null;
  cancelled: boolean;
  live: boolean;
  schedulePublished: boolean;
  stages: PublicFestivalStage[];
}

export interface FestivalDay {
  /** "2026-10-31", the day in the festival's zone. */
  key: string;
  /** "Saturday, October 31". */
  label: string;
  stages: PublicFestivalStage[];
}

const zoneOf = (festival: { timezone: string | null }): string =>
  normalizeTimeZone(festival.timezone) ?? 'UTC';

function dayKey(instant: string, zone: string): string {
  const wall = instantToWallClock(new Date(instant), zone);
  if (!wall) return instant.slice(0, 10);
  return `${wall.year}-${String(wall.month).padStart(2, '0')}-${String(wall.day).padStart(2, '0')}`;
}

/** The stages grouped by the day they start, in the festival's own zone. */
export function festivalDays(festival: PublicFestival): FestivalDay[] {
  const zone = zoneOf(festival);
  const days = new Map<string, FestivalDay>();
  for (const stage of [...festival.stages].sort((a, b) => a.startsAt.localeCompare(b.startsAt))) {
    const key = dayKey(stage.startsAt, zone);
    let day = days.get(key);
    if (!day) {
      day = {
        key,
        label: new Date(stage.startsAt).toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric', timeZone: zone,
        }),
        stages: [],
      };
      days.set(key, day);
    }
    day.stages.push(stage);
  }
  return [...days.values()];
}

/** "Oct 31", or "Oct 30 – Nov 1" across days, in the festival's zone. Empty with no stages. */
export function festivalDatesLabel(festival: PublicFestival): string {
  if (festival.stages.length === 0) return '';
  const zone = zoneOf(festival);
  const starts = festival.stages.map((s) => s.startsAt).sort();
  const fmt = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: zone });
  const first = fmt(starts[0]);
  const last = fmt(starts[starts.length - 1]);
  const year = new Date(starts[starts.length - 1]).toLocaleDateString('en-US', { year: 'numeric', timeZone: zone });
  return first === last ? `${first}, ${year}` : `${first} – ${last}, ${year}`;
}

/** "8:00 PM" in the festival's zone, or null. */
export function clockLabel(iso: string | null | undefined, festival: { timezone: string | null }): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: zoneOf(festival) });
}

/** "8:00 PM – 11:00 PM", "8:00 PM", or null: a stage's hours or an act's set. */
export function rangeLabel(
  start: string | null | undefined,
  end: string | null | undefined,
  festival: { timezone: string | null },
): string | null {
  const a = clockLabel(start, festival);
  if (!a) return null;
  const b = clockLabel(end, festival);
  return b ? `${a} – ${b}` : a;
}

/** The stages a fan can join right now. */
export function liveStages(festival: PublicFestival): PublicFestivalStage[] {
  return festival.stages.filter((s) => s.live && s.roomCode);
}
